import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createVoiceRecorder, type VoiceRecorderState } from './voiceRecorder.js';
import { createLevelMeter, type LevelMeter } from './voiceLevel.js';

/**
 * VOICE-001 — the microphone as a React hook: a thin skin over the plain core
 * in voiceRecorder.ts, which holds the lifecycle and is tested without React.
 *
 * `onAutoStop` gets the note when the limit stopped it, since nobody is waiting
 * on stop() then. It is read through a ref so an inline arrow does not rebuild
 * the recorder in the middle of a take.
 *
 * `level()` is the microphone's loudness for the orb to draw. The meter opens
 * when a take starts and closes when it ends, on the recorder's own stream; it
 * is kept in a ref because the orb reads it every frame and nothing on the
 * React side ever needs to re-render for it.
 */
export function useVoiceRecorder(maxSeconds: number, onAutoStop?: (blob: Blob | null) => void) {
  const [state, setState] = useState<VoiceRecorderState>('idle');
  const [seconds, setSeconds] = useState(0);
  const autoStop = useRef(onAutoStop);
  autoStop.current = onAutoStop;
  const meter = useRef<LevelMeter | null>(null);

  const core = useMemo(
    () =>
      createVoiceRecorder({
        getUserMedia: () => navigator.mediaDevices.getUserMedia({ audio: true }),
        MediaRecorderCtor: MediaRecorder,
        isTypeSupported: (mime) => MediaRecorder.isTypeSupported(mime),
        maxSeconds,
        onTick: setSeconds,
        onState: setState,
        onAutoStop: (blob) => autoStop.current?.(blob),
      }),
    [maxSeconds],
  );

  useEffect(() => core.dispose, [core]);

  // Paused keeps the meter: the stream is still open, and resume should find
  // the orb already listening rather than a frame late.
  const live = state === 'armed' || state === 'recording' || state === 'paused';
  useEffect(() => {
    if (!live) return undefined;
    const stream = core.stream();
    if (!stream) return undefined;
    meter.current = createLevelMeter(stream);
    return () => {
      meter.current?.close();
      meter.current = null;
    };
  }, [live, core]);

  const level = useCallback(() => meter.current?.level() ?? 0, []);

  return {
    state,
    seconds,
    start: core.start,
    activate: core.activate,
    stop: core.stop,
    pause: core.pause,
    resume: core.resume,
    discard: core.discard,
    level,
    stream: core.stream,
  };
}
